import React, { useState, useEffect } from 'react'
import { FaCheck, FaPlus, FaTrash } from 'react-icons/fa'
import { Alert, Box, Stack, ResponsiveStack, Button, Heading, Text, FormControl, Input, Select, SearchableSelect, Textarea, Icon, Badge, SimpleModal, SimpleTable, EmptyBox, ConfirmButton, UserChip, UserChooser, Checkbox, Card, Tag, Wrap, WrapItem } from '@/components/lib'
import { LuSparkles } from 'react-icons/lu'
import utils from '@/utils/utils'
import api from '../../../api'
import useStore from '../../../store'

import CheckboxesImage from '../../../images/checkboxes.png'

export default function Automatic () {
  const [automaticQueries, setAutomaticQueries] = useState([])
  const [smartInstructions, setSmartInstructions] = useState('')
  const { trial, team, dataTypes, stages, sites, groups, getTrialUsers, updateTrial } = useStore()
  const trialId = trial?._id
  const users = getTrialUsers(trialId)
  const instructionsChanged = smartInstructions !== (trial?.smartAutomaticQueryInstructions || '')

  useEffect(() => {
    if (!trialId) return
    api.automaticQueries.getForTrial(trialId, d => setAutomaticQueries(d.automaticQueries))
  }, [trialId])

  useEffect(() => {
    setSmartInstructions(trial?.smartAutomaticQueryInstructions || '')
  }, [trial?._id, trial?.smartAutomaticQueryInstructions])

  function enableSmartQueries (enabled) {
    api.trials.update(trialId, { smartAutomaticQueriesEnabled: enabled }, t => updateTrial(t._id, t), err => utils.toast('error', err.message))
  }

  function saveSmartQueryInstructions () {
    api.trials.update(trialId, { smartAutomaticQueryInstructions: smartInstructions }, t => {
      updateTrial(t._id, { smartAutomaticQueryInstructions: t.smartAutomaticQueryInstructions || '' })
      utils.toast('success', 'Instructions saved')
    }, err => utils.toast('error', err.message))
  }

  function updateSmartQueryAssignees (assignees) {
    const ids = assignees || []
    api.trials.update(trialId, { smartAutomaticQueryAssignees: ids }, t => {
      updateTrial(t._id, { smartAutomaticQueryAssignees: t.smartAutomaticQueryAssignees })
    }, err => utils.toast('error', err.message))
  }

  function addAutomaticQuery (automaticQuery) {
    const newAutomaticQueries = [...automaticQueries]
    if (newAutomaticQueries.find(aq => aq._id === automaticQuery._id)) {
      newAutomaticQueries[newAutomaticQueries.findIndex(aq => aq._id === automaticQuery._id)] = automaticQuery
    } else {
      newAutomaticQueries.push(automaticQuery)
    }
    setAutomaticQueries(newAutomaticQueries)
  }

  function deleteAutomaticQuery (automaticQueryId) {
    api.automaticQueries.delete(automaticQueryId, () => {
      setAutomaticQueries(automaticQueries.filter(aq => aq._id !== automaticQueryId))
      utils.toast('success', 'Automatic query deleted')
    }, err => {
      utils.toast('error', 'Unable to delete automatic query', err.message)
    })
  }

  return (
    <Stack gap={10}>

      {utils.hasFeature(team, 'smartAutomaticQueries') &&
        <Card variant='elevated' gap={5}>
          <Stack direction='row' justifyContent='space-between'>
            <Box>
              <Heading as='h3' size='md'>"Smart" automatic queries</Heading>
              <Text>Trialflare can generate queries automatically if it detects potentially anomalous results.</Text>
            </Box>
          </Stack>

          <Alert colorPalette='purple' variant='surface' withIcon={false}>
            <Stack direction='row'>
              <Icon as={LuSparkles} boxSize={6} mr={2} />
              <Stack>
                <Heading size='sm'>About this feature</Heading>
                <Text>Trialflare AI can make mistakes. This feature should be used as a helpful tool and not relied upon entirely.</Text>
              </Stack>
            </Stack>
          </Alert>

          <Checkbox isChecked={trial?.smartAutomaticQueriesEnabled} onChange={e => enableSmartQueries(e.checked)}>Enable smart automatic queries</Checkbox>
          {trial?.smartAutomaticQueriesEnabled &&
            <FormControl label='Additional instructions' helperText='Optional. Tell Trialflare what to pay attention to or ignore in this trial — for example "ignore the age data type" or "weights below 40kg are expected in this cohort".'>
              <Stack direction='row' alignItems='center'>
                <Textarea
                  placeholder='e.g. Ignore the "age" data type.'
                  rows={3}
                  maxLength={2000}
                  value={smartInstructions}
                  width='400px'
                  onChange={e => setSmartInstructions(e.target.value)}
                />
                <Button size='sm' colorPalette='primary' disabled={!instructionsChanged} onClick={saveSmartQueryInstructions}>Save instructions</Button>
              </Stack>
            </FormControl>}
          {trial?.smartAutomaticQueriesEnabled &&
            <FormControl label='Assign new smart automatic queries to'>
              <UserChooser
                multiple
                placeholder='No one (queries will be unassigned)'
                users={users}
                selectedUsers={trial?.smartAutomaticQueryAssignees || []}
                onChosen={newAssignees => updateSmartQueryAssignees(newAssignees)}
              />
            </FormControl>}
        </Card>}

      <Card variant='elevated' gap={5}>
        <Stack direction='row' justifyContent='space-between'>
          <Box>
            <Heading as='h3' size='md'>Rule-based automatic queries</Heading>
            <Text>Set up rules to automatically generate queries.</Text>
          </Box>
          <AutomaticQueryEditor
            trigger={<Button colorPalette='primary'>Create a new automatic query</Button>}
            onChange={addAutomaticQuery}
            trialId={trialId}
            users={users}
            dataTypes={dataTypes}
            stages={stages}
            groups={groups}
            sites={sites}
          />
        </Stack>

        {automaticQueries.length > 0
          ? (
            <Box>
              <SimpleTable
                headers={['Name', 'Data type', 'Rules', 'Assign to', '']}
                rows={automaticQueries.map(aq => [
                  aq.name,
                  dataTypes.find(d => d._id === aq.dataType)?.name,
                  aq.rules?.length || 0,
                  (aq.query?.assigneesUsers?.length > 0)
                    ? (
                      <Stack direction='row' gap={2} wrap='wrap'>
                        {aq.query.assigneesUsers.map(u => (
                          <UserChip key={u._id} compact user={u} />
                        ))}
                      </Stack>
                      )
                    : <Badge colorPalette='blue'>No auto-assign</Badge>,
                  <Stack key={aq._id} direction='row' justifyContent='flex-end'>
                    <AutomaticQueryEditor
                      trigger={<Button colorPalette='primary' variant='outline' size='sm'>Edit</Button>}
                      automaticQuery={aq}
                      onChange={addAutomaticQuery}
                      users={users}
                      dataTypes={dataTypes}
                      stages={stages}
                      groups={groups}
                      sites={sites}
                    />
                    <ConfirmButton
                      trigger={<Button size='sm' variant='ghost'><Icon><FaTrash /></Icon></Button>}
                      header='Really delete this automatic query?'
                      footer='This cannot be undone.'
                      onConfirm={e => deleteAutomaticQuery(aq._id)}
                    />
                  </Stack>
                ])}
              />
            </Box>)
          : (
            <EmptyBox icon='robot' title='No automatic queries have been created yet' description='Set up queries that are automatically raised based on rules you set.' />)}

      </Card>
    </Stack>
  )
}

function AutomaticQueryEditor ({ trigger, trialId, automaticQuery, onChange, users, dataTypes, stages, groups, sites }) {
  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState(automaticQuery?.name ?? '')
  const [description, setDescription] = useState(automaticQuery?.description ?? '')
  const [dataType, setDataType] = useState(automaticQuery?.dataType ?? '')
  const [rules, setRules] = useState(automaticQuery?.rules || [])
  const [query, setQuery] = useState(automaticQuery?.query || {})

  function save () {
    const queryData = { title: query.title, description: query.description, assignees: query.assignees || [] }
    const data = { name, description, dataType, rules, query: queryData }
    if (automaticQuery) {
      api.automaticQueries.update(automaticQuery._id, data, d => {
        setIsOpen(false)
        onChange(d)
      }, err => {
        utils.toast('error', 'Unable to save automatic query', err.message)
      })
    } else {
      api.automaticQueries.create(trialId, data, d => {
        setIsOpen(false)
        onChange(d)
      }, err => {
        utils.toast('error', 'Unable to create automatic query', err.message)
      })
    }
  }

  function addRule (rule) {
    if (rule.delete) {
      setRules(rules.filter(r => r.id !== rule.delete))
      return
    }
    const newRules = [...rules]
    if (newRules.find(r => r.id === rule.id)) {
      newRules[newRules.findIndex(r => r.id === rule.id)] = rule
    } else {
      newRules.push(rule)
    }
    setRules(newRules)
  }

  function updateQuery (update) {
    setQuery({ ...query, ...update })
  }

  return (
    <>
      {React.cloneElement(trigger, { onClick: () => setIsOpen(true) })}
      <SimpleModal
        size='xl'
        isOpen={isOpen} onClose={() => setIsOpen(false)}
        header={automaticQuery ? 'Edit automatic query' : 'Create automatic query'}
        body={
          <Stack spacing={5}>
            <ResponsiveStack spacing={5}>
              <Stack gap={3} p={3} rounded='md' bg='bg.surface' maxW='400px'>
                <Heading size='md'>General</Heading>
                <FormControl label='Name'>
                  <Input placeholder='My automatic query' type='text' value={name || ''} onChange={e => setName(e.target.value)} />
                </FormControl>
                <FormControl label='Description'>
                  <Textarea placeholder='Provide a brief description' value={description || ''} onChange={e => setDescription(e.target.value)} />
                </FormControl>
                <FormControl label='Data type'>
                  <SearchableSelect
                    placeholder='Select a data type'
                    value={dataType || ''}
                    onChange={selected => setDataType(selected)}
                    options={dataTypes?.map(d => ({ value: d._id, label: `${d.name} (${d.type})` })) || []}
                  />
                </FormControl>
              </Stack>
              <Stack gap={3} p={3} flex={1} borderWidth='1px' rounded='md'>
                <Box>
                  <Heading size='md'>Rules</Heading>
                  <Text>When all of the conditions below are met, a query will be raised.</Text>
                </Box>

                {rules?.length > 0
                  ? (
                    <Stack direction='column' spacing={3}>
                      {rules.map((rule) => <RuleEditor key={rule.id} rule={rule} dataType={dataTypes?.find(d => d._id === dataType)} onChange={addRule} dataTypes={dataTypes} stages={stages} groups={groups} sites={sites} />)}
                      <Button colorPalette='blue' size='sm' variant='outline' onClick={() => addRule({ id: utils.uuidv4() })}>Add a rule</Button>
                    </Stack>)
                  : (
                    <EmptyBox
                      image={CheckboxesImage} title='No rules have been set yet' description='Set up rules that define when a query should be raised.'
                    >
                      <Button colorPalette='primary' variant='outline' onClick={() => addRule({ id: utils.uuidv4() })}>Add the first rule</Button>
                    </EmptyBox>)}

              </Stack>
            </ResponsiveStack>
            <Stack gap={3} p={3} rounded='md' borderWidth='1px'>
              <Box>
                <Heading size='md'>Query template</Heading>
                <Text>Define the query to be raised when the rules are met.</Text>
              </Box>

              <FormControl label='Query title'>
                <Input placeholder='My query' type='text' value={query?.title || ''} onChange={e => updateQuery({ title: e.target.value })} />
              </FormControl>
              <FormControl label='Query description'>
                <Textarea placeholder='Provide a brief description' value={query?.description || ''} onChange={e => updateQuery({ description: e.target.value })} />
              </FormControl>
              <FormControl label='Auto-assign query to'>
                <UserChooser
                  multiple
                  placeholder='Do not auto-assign'
                  users={users}
                  selectedUsers={query?.assignees || []}
                  onChosen={newAssignees => updateQuery({ assignees: newAssignees || [] })}
                />
              </FormControl>
            </Stack>
          </Stack>
}
        footer={
          <Stack direction='row' justifyContent='end'>
            <Button onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button onClick={save} colorPalette='primary'>Save</Button>
          </Stack>
}
      />
    </>
  )
}

function RuleEditor ({ rule, onChange, dataType, stages, groups, sites }) {
  function updateRule (update) {
    onChange({ ...rule, ...update })
  }

  const selectedStage = stages?.find(s => s._id === rule.stage)

  return (
    <Stack direction='row' p={2} bg='bg.subtle' rounded='sm' alignItems='center'>
      <Select size='sm' placeholder='Rule type...' value={rule.ruleType || ''} onChange={e => updateRule({ ruleType: e.target.value })}>
        <option value='value'>Data type value</option>
        {dataType?.type === 'promisForm' && <option value='promisTScore'>PROMIS T score</option>}
        {dataType?.type === 'promisBattery' && <option value='promisPropr'>PROPr score</option>}
        <option value='stage'>Trial stage</option>
        <option value='group'>Participant group</option>
        <option value='site'>Participant site</option>
      </Select>

      {rule.ruleType === 'value' &&
        <>
          <Select
            size='sm' placeholder='Select an operator' value={rule.operator || ''} onChange={e => {
              const operator = e.target.value
              const keepsOptions = ['includes', 'notIncludes'].includes(operator) && ['includes', 'notIncludes'].includes(rule.operator)
              updateRule(keepsOptions ? { operator } : { operator, value: ['includes', 'notIncludes'].includes(operator) ? [] : '' })
            }}
          >
            <option value='exists'>Exists</option>
            <option value='notExists'>Does not exist</option>
            {dataType?.type === 'choice' &&
              <>
                <option value='includes'>Is one of</option>
                <option value='notIncludes'>Is none of</option>
              </>}
            {['integer', 'float', 'slider', 'likert', 'weight', 'height'].includes(dataType?.type) &&
              <>
                <option value='equals'>Equals</option>
                <option value='notEquals'>Does not equal</option>
                <option value='greaterThan'>Greater than</option>
                <option value='lessThan'>Less than</option>
              </>}
          </Select>
          {['includes', 'notIncludes'].includes(rule.operator) &&
            <ChoiceOptionPicker
              dataType={dataType}
              selected={Array.isArray(rule.value) ? rule.value : (rule.value ? [rule.value] : [])}
              onChange={options => updateRule({ value: options })}
            />}
          {['equals', 'notEquals', 'greaterThan', 'lessThan'].includes(rule.operator) &&
            <Input size='xs' placeholder={dataType?.type === 'likert' ? 'Total, e.g. 12' : 'Value'} type='text' value={rule.value ?? ''} onChange={e => updateRule({ value: e.target.value })} />}
          {dataType?.type === 'likert' && ['equals', 'notEquals', 'greaterThan', 'lessThan'].includes(rule.operator) &&
            <Text fontSize='xs' color='fg.muted' flexShrink={0}>total across statements</Text>}
        </>}

      {['promisTScore', 'promisPropr'].includes(rule.ruleType) &&
        <>
          <Select size='sm' placeholder='Select an operator' value={rule.operator || ''} onChange={e => updateRule({ operator: e.target.value })}>
            <option value='exists'>Exists</option>
            <option value='notExists'>Does not exist</option>
            <option value='equals'>Equals</option>
            <option value='notEquals'>Does not equal</option>
            <option value='greaterThan'>Greater than</option>
            <option value='lessThan'>Less than</option>
          </Select>
          {['equals', 'notEquals', 'greaterThan', 'lessThan'].includes(rule.operator) &&
            <Input size='xs' placeholder={rule.ruleType === 'promisPropr' ? 'e.g. 0.5' : 'e.g. 50'} type='text' value={rule.value ?? ''} onChange={e => updateRule({ value: e.target.value })} />}
        </>}

      {rule.ruleType === 'stage' &&
        <Select
          size='sm' placeholder='Select a stage' value={rule.stage || ''} onChange={e => updateRule({
            stage: e.target.value,
            stageRecurrence: null
          })}
        >
          {stages?.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </Select>}

      {rule.ruleType === 'stage' && selectedStage?.isRecurring &&
        <Select size='sm' placeholder='All recurrences' value={rule.stageRecurrence ?? ''} onChange={e => updateRule({ stageRecurrence: e.target.value === '' ? null : e.target.value })}>
          {Array.from({ length: selectedStage.recurringDuration }, (_, i) => i).map(recurrence =>
            <option value={`${recurrence}`} key={`${recurrence + 1}`}>Recurrence {recurrence + 1}</option>)}
        </Select>}

      {rule.ruleType === 'group' &&
        <Select size='sm' placeholder='Select a group' value={rule.group || ''} onChange={e => updateRule({ group: e.target.value })}>
          {groups?.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
        </Select>}

      {rule.ruleType === 'site' &&
        <Select size='sm' placeholder='Select a site' value={rule.site || ''} onChange={e => updateRule({ site: e.target.value })}>
          {sites?.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </Select>}

      <ConfirmButton
        trigger={<Button size='sm' variant='ghost'><Icon as={FaTrash} /></Button>}
        header='Really delete this rule?'
        onConfirm={e => onChange({ delete: rule.id })}
      />
    </Stack>
  )
}

// The options of a choice field, as toggleable tags. A choice answer is the
// list of options the participant selected, so a rule names the options it is
// about rather than comparing against one typed-in value. Mirrors how a
// conditional field picks the options it appears for.
function ChoiceOptionPicker ({ dataType, selected, onChange }) {
  // Options the rule already targets are always shown, even if they have since
  // been removed from the data type — the rule still evaluates against them, so
  // hiding them would leave a rule nobody can see the whole of, or unpick.
  const configured = [...(dataType?.options || []), ...(dataType?.includeNone ? ['none'] : [])]
  const options = [...configured, ...selected.filter(o => !configured.includes(o))]

  function toggle (option) {
    onChange(selected.includes(option)
      ? selected.filter(o => o !== option)
      : [...selected, option])
  }

  if (!options.length) {
    return <Text fontSize='xs' color='fg.muted'>This data type has no options configured.</Text>
  }

  return (
    <Wrap gap={2}>
      {options.map(option => {
        const isSelected = selected.includes(option)
        return (
          <WrapItem key={option}>
            <Tag
              cursor='pointer'
              colorPalette={isSelected ? 'primary' : 'gray'}
              onClick={() => toggle(option)}
            ><Icon as={isSelected ? FaCheck : FaPlus} /> {option === 'none' ? 'None' : option}{!configured.includes(option) && ' (removed)'}
            </Tag>
          </WrapItem>
        )
      })}
    </Wrap>
  )
}
